import { useState, type FormEvent } from 'react';
import { ApiRequestError } from '../api.ts';
import { useSession } from '../session.tsx';
import { Banner, Button, Card, Field } from '../components/ui.tsx';

export function SignIn({ notice }: { notice?: string | null }) {
  const { signIn, recoverAsAdmin } = useSession();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);

  async function attempt(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Could not reach the server.',
      );
    } finally {
      setBusy(false);
    }
  }

  function onSubmitCode(event: FormEvent) {
    event.preventDefault();
    void attempt(() => signIn(code));
  }

  function onSubmitPassword(event: FormEvent) {
    event.preventDefault();
    void attempt(() => recoverAsAdmin(password));
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-12">
      <header className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
      </header>

      {notice && <Banner tone="danger">{notice}</Banner>}

      <Card>
        <form onSubmit={onSubmitCode} className="flex flex-col gap-4">
          <Field
            label="Access code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="Enter code"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            // Phones default to a keyboard with no visible hyphen; this is the
            // one that shows letters and punctuation together.
            inputMode="text"
            enterKeyHint="go"
            autoFocus
            required
            {...(error ? { error } : {})}
          />
          <Button type="submit" loading={busy}>
            Continue
          </Button>
        </form>
      </Card>

      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            setShowRecovery((open) => !open);
            setError(null);
          }}
          className="text-sm text-text-faint underline-offset-4 hover:text-text-muted hover:underline"
        >
          {showRecovery ? 'Use access code' : 'Need help?'}
        </button>
      </div>

      {showRecovery && (
        <Card>
          <form onSubmit={onSubmitPassword} className="flex flex-col gap-4">
            <Field
              label="Admin password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
            <Button type="submit" variant="secondary" loading={busy}>
              Continue
            </Button>
          </form>
        </Card>
      )}
    </main>
  );
}
